import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { SwalToastService } from 'src/app/services/swal.service';
import { IAddNutritionIntake } from 'src/app/interfaces/NutritionIntake/IAddNutritionIntake';
import { INutritionIntakeDropdown } from 'src/app/interfaces/NutritionIntake/INutritionIntakeDropdown';
import { NutritionIntakeService } from 'src/app/services/nutritionIntake.service';
import { NzSliderModule } from 'ng-zorro-antd/slider';
import { dateRangeValidator } from 'src/date/date.configuration';

@Component({
  selector: 'app-nutrition-intake-add-edit',
  standalone: true,
  templateUrl: './nutrition-intake-add-edit.component.html',
  styleUrls: ['./nutrition-intake-add-edit.component.css'],
  imports: [
    NzModalModule,
    TranslateModule,
    FormsModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    NzSelectModule,
    CommonModule,
    NzButtonModule,
    NzTableModule,
    NzLayoutModule,
    NzIconModule,
    NzSliderModule,
  ],
})
export class NutritionIntakeAddEditComponent {
  private destroy$ = new Subject<void>();

  isVisible = false;
  isLoading = false;
  isDropdownLoading = false;

  isEdit = false;
  editNutritionIntakeId?: number;

  nutritionIntakeSubmitted = false;

  dropdown: INutritionIntakeDropdown = {};

  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef; // Reference to the modal

  nutritionIntakeForm: FormGroup = new FormGroup({});

  nzMarks = {
    100: '100',
    75: '¾',
    50: '½',
    25: '¼',
    0: '0',
  };

  constructor(
    private readonly nutritionIntakeService: NutritionIntakeService,
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.isEdit = false;
    this.isLoading = true;
    this.isDropdownLoading = true;

    this.nutritionIntakeForm = this.formBuilder.group({
      dateValue: [
        null,
        [Validators.required, dateRangeValidator(undefined, new Date())],
      ],
      foodTypeValue: [null, Validators.required],
      portionEatenPercentageValue: [100, Validators.required],
      portionSizeValue: [
        null,
        Validators.compose([Validators.required, Validators.min(0)]),
      ],
      descriptionValue: [null],
    });

    this.nutritionIntakeService.getNutritionIntakeDropdown().subscribe({
      next: (pro) => {
        this.isDropdownLoading = false;
        this.dropdown = pro?.data;
      },
      error: (err) => {},
    });

    this.nutritionIntakeService.nutritionIntakeEditSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        // GET DATA FIELDS AND ASSIGN DEFAULT VALUES TO FORM
        if (data) {
          this.isEdit = true;
          this.isLoading = false;

          this.editNutritionIntakeId = data?.id;

          this.nutritionIntakeForm.patchValue({
            dateValue:
              data?.onSetDateTime && this.convertToLocale(data?.onSetDateTime),
            foodTypeValue: data?.foodTypeId,
            portionEatenPercentageValue: data?.portionEatenPercentage,
            portionSizeValue: data?.portionSize,
            descriptionValue: data?.description,
          });
        } else {
          this.isEdit = false;
          this.isLoading = false;

          this.editNutritionIntakeId = undefined;

          this?.nutritionIntakeForm.reset();

          this.nutritionIntakeForm.patchValue({
            dateValue: this.convertToLocale(),
            portionEatenPercentageValue: 100,
          });
        }
      });

    this.nutritionIntakeService.nutritionIntakeModalSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((showModal) => {
        this.isVisible = showModal;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    this?.modalRef.destroy();
  }

  handleCancel() {
    this.nutritionIntakeForm.reset();

    this.nutritionIntakeService.nutritionIntakeModalSub.next(false);

    this.nutritionIntakeSubmitted = false;

    this.isLoading = false;

    this.modalRef.destroy();
  }

  submitForm() {
    console.log(this.nutritionIntakeForm);
    this.isLoading = true;
    this.nutritionIntakeSubmitted = true;

    if (this.nutritionIntakeForm.valid) {
      const nutritionIntakeObj: IAddNutritionIntake = {};

      nutritionIntakeObj.id = this.editNutritionIntakeId;

      nutritionIntakeObj.onSetDateTime = moment(
        this.nutritionIntakeForm.value?.dateValue
      ).format();

      nutritionIntakeObj.foodTypeId =
        this.nutritionIntakeForm.get('foodTypeValue')?.value;
      nutritionIntakeObj.portionEatenPercentage = this.nutritionIntakeForm.get(
        'portionEatenPercentageValue'
      )?.value;
      nutritionIntakeObj.portionSize =
        this.nutritionIntakeForm.get('portionSizeValue')?.value;
      nutritionIntakeObj.description =
        this.nutritionIntakeForm.get('descriptionValue')?.value;

      if (this.isEdit) {
        this.nutritionIntakeService
          .editNutritionIntake(nutritionIntakeObj)
          .subscribe({
            next: () => {
              this.swalToastService.toastSuccess(
                'Nutrition Intake record edited successfully'
              );
              this.nutritionIntakeService.getData();

              this.handleCancel();
            },
            error: (err) => {
              this.swalToastService.toastError(
                'Unable to edit Nutrition Intake record'
              );

              this.handleCancel();
            },
          });
      } else {
        this.nutritionIntakeService
          .addNutritionIntake(nutritionIntakeObj)
          .subscribe({
            next: () => {
              this.swalToastService.toastSuccess(
                'Nutrition Intake record added successfully'
              );
              this.nutritionIntakeService.getData();

              this.handleCancel();
            },
            error: (err) => {
              this.swalToastService.toastError(
                'Unable to add Nutrition Intake record'
              );
            },
          });
      }
    } else {
      this.isLoading = false;
    }
  }

  getCurrentDateTime() {
    return new Date().toISOString().slice(0, 16);
  }

  // CONVERT UTC TO LOCAL
  convertToLocale(date?: string) {
    const currentDateTime = date ? moment(date).format() : moment().format();

    const isoDate = currentDateTime.slice(0, 16);

    return isoDate;
  }

  formatPercentage = (value: number): string => `${value}%`;
}
